// ============================================================
//  ★ 데이터 SEAM — 개발자 작업은 "여기"만 ★
// ------------------------------------------------------------
//  변환 산출물의 유일한 데이터 접점. fn 내부를 2.0 API 호출로 교체했다.
//  (핸드오프 규칙 — 컬럼/필터/테이블 UI 는 훅 계약(contents/totalCount) 그대로 소비)
//  BE: GET /api/v1/authorities (LOGISPRD-9616, MOMS_AUTH_INFO 헤드)
// ============================================================
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import request from '@/utils/request'

export interface AuthManagementRow {
  SPR_NM: string
  AUTH_CD: string
  AUTH_NM: string
}

export interface AuthManagementParams {
  shipperCode?: string
  /** 권한코드 필터 — 다건 입력(string[]). 2.0 API 파라미터에 없어 클라이언트 contains(OR) 필터 */
  authCodes?: string[]
}

export interface AuthManagementResponse {
  contents: AuthManagementRow[]
  totalCount: number
}

interface AuthorityItem {
  shipperCode: string
  authCode: string
  authName: string
}

const fn = async (params: AuthManagementParams): Promise<AuthManagementResponse> => {
  const res = await request.get<ApiResponseV2<{ items: AuthorityItem[] }>, { shipperCode?: string }>('/api/v1/authorities', {
    shipperCode: params.shipperCode || undefined,
  })
  // 권한코드 필터는 2.0 API 파라미터에 없어 클라이언트 contains(OR) 필터 (신규 디자인 필터 스펙)
  const qs = (params.authCodes ?? []).map((v) => v.trim().toLowerCase()).filter(Boolean)
  const contents = (res.data?.items ?? [])
    .filter((i) => !qs.length || qs.some((q) => i.authCode.toLowerCase().includes(q)))
    .map((i) => ({ SPR_NM: i.shipperCode, AUTH_CD: i.authCode, AUTH_NM: i.authName }))
  return { contents, totalCount: contents.length }
}

/** Query key — 도메인별 queryKeys 객체(CLAUDE.md). 무효화는 all, 조회는 list(params). 화주 옵션은 유저관리 API 재사용이라 여기 함께 둔다. */
export const authManagementQueryKeys = {
  all: ['getAuthManagementList'] as const,
  list: (params: AuthManagementParams) => [...authManagementQueryKeys.all, params] as const,
  shipperOptions: ['MA03_SHIPPER_OPTIONS'] as const,
}

export const useGetAuthManagementList = (params: AuthManagementParams, enabled = true): UseQueryResult<AuthManagementResponse, Error> =>
  useQuery({
    queryKey: authManagementQueryKeys.list(params),
    queryFn: () => fn(params),
    enabled,
    meta: { noErrorMessage: true }, // 실패 안내는 화면이 새 디자인 알럿으로 한 번 낸다(usePboQueryErrorToast → notice.failure — 옛 전역 알럿 대신)
  })
